import { useEffect, useRef, useState } from "react";
import { useDebouncedValue } from "@/lib/use-debounced-value";

export interface GeocodeResult {
  name: string;
  country: string;
  state?: string;
  lat: number;
  lon: number;
}

// Module-scoped cache shared across all hook instances. Geocode results don't
// change quickly so this is safe for the lifetime of the page.
const cache = new Map<string, GeocodeResult[]>();
const inflight = new Map<string, Promise<GeocodeResult[]>>();

async function fetchGeocode(query: string): Promise<GeocodeResult[]> {
  const key = query.trim().toLowerCase();
  const cached = cache.get(key);
  if (cached) return cached;
  const existing = inflight.get(key);
  if (existing) return existing;

  const promise = (async () => {
    try {
      const res = await fetch(`/api/geocode?q=${encodeURIComponent(query)}`);
      if (!res.ok) {
        // Don't poison the cache on transient errors (429/5xx). Caller will retry next time.
        return [];
      }
      const data = (await res.json()) as { results?: GeocodeResult[] };
      const results = data.results ?? [];
      cache.set(key, results);
      return results;
    } catch {
      return [];
    } finally {
      inflight.delete(key);
    }
  })();
  inflight.set(key, promise);
  return promise;
}

/**
 * Returns geocode results for the current query, debounced and cached.
 * Set `enabled` to false (e.g. when search is closed) to avoid extra requests.
 */
export function useGeocode(query: string, options: { delayMs?: number; minLength?: number; enabled?: boolean } = {}) {
  const delay = options.delayMs ?? 350;
  const minLength = options.minLength ?? 2;
  const enabled = options.enabled ?? true;
  const debounced = useDebouncedValue(query.trim(), delay);
  const [results, setResults] = useState<GeocodeResult[]>([]);
  const [loading, setLoading] = useState(false);
  const tokenRef = useRef(0);

  useEffect(() => {
    if (!enabled || debounced.length < minLength) {
      setResults([]);
      setLoading(false);
      return;
    }
    const myToken = ++tokenRef.current;
    setLoading(true);
    fetchGeocode(debounced).then((res) => {
      // Drop stale responses
      if (myToken !== tokenRef.current) return;
      setResults(res);
      setLoading(false);
    });
  }, [debounced, enabled, minLength]);

  return { results, loading };
}
